import React, { createContext, useContext, useCallback } from 'react';
import { useAccount, useBalance, useChainId, useDisconnect, useSwitchChain } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { base, arbitrum, polygon } from 'wagmi/chains';

const Web3Context = createContext(null);

// Supported chains for Bet4Peer
const SUPPORTED_CHAINS = {
  [base.id]: 'Base',
  [arbitrum.id]: 'Arbitrum',
  [polygon.id]: 'Polygon',
};

export function Web3Provider({ children }) {
    const { address, isConnected, isConnecting } = useAccount();
    const chainId = useChainId();
    const { disconnect } = useDisconnect();
    const { switchChain } = useSwitchChain();
    const { openConnectModal } = useConnectModal();
    
    const { data: balanceData } = useBalance({
        address: address,
    });

    const balance = balanceData ? parseFloat(balanceData.formatted) : 0;
    const isMainnet = chainId === base.id || chainId === arbitrum.id || chainId === polygon.id;
    const chainName = SUPPORTED_CHAINS[chainId] || 'Unsupported';
    const isCorrectChain = chainId === base.id; // Primary chain is Base Mainnet

    const connect = useCallback(() => {
        if (openConnectModal) {
            openConnectModal();
        }
    }, [openConnectModal]);

    const switchToBase = useCallback(async () => {
        if (chainId !== base.id) {
            try {
                await switchChain({ chainId: base.id });
            } catch (error) {
                console.error('Failed to switch network:', error);
            }
        }
    }, [chainId, switchChain]);

    const value = {
        address,
        isConnected,
        isConnecting,
        balance,
        chainId,
        chainName,
        isMainnet,
        isCorrectChain,
        connect,
        disconnect,
        switchToBase,
    };

    return (
        <Web3Context.Provider value={value}>
            {children}
        </Web3Context.Provider>
    );
}

export function useWeb3() {
    const context = useContext(Web3Context);
    if (!context) {
        throw new Error('useWeb3 must be used within a Web3Provider');
    }
    return context;

}

// Alias for backwards compatibility
export const useWallet = useWeb3;

// Utility to format address
export function formatAddress(address) {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// Utility to format ETH
export function formatETH(amount, decimals = 4) {
    if (amount === null || amount === undefined) return '0';
    return parseFloat(amount).toFixed(decimals);
}
